import { useEffect, useMemo, useRef, useState } from "react";
import { type AxiosInstance, isAxiosError } from "axios";
import * as Sentry from "@sentry/react";
import { getCompletePropertyRows } from "@/utils/propertyRows";
import { type PropertyLocationRow } from "@/types/propertyLocationRow";
import {
  MAX_ACCESS_ADDRESS_LENGTH,
  MAX_ACCESS_ROWS,
  RealEstateResearchRequestAPI,
} from "../api/realEstateResearchRequestAPI";
import { type ResearchAccess } from "../types";

/** 地番の入力が止まってから判定を問い合わせるまでの待ち時間（ミリ秒） */
const DEBOUNCE_MS = 500;

export const NOT_JUDGED_ACCESS: ResearchAccess = {
  judged: false,
  basis: "",
  stationName: "",
  lineName: "",
  onFoot: null,
  remoteArea: false,
  vehicleRequired: false,
};

export interface ResearchAccessState {
  access: ResearchAccess;
  /** 今の地番での判定を問い合わせ中 */
  loading: boolean;
}

/**
 * 入力済みの地番から、僻地加算・車両必須加算の判定を引く。地番の土地が見つからなければ、対象住所の位置で判定される。
 * 判定は地番と対象住所の内容ごとに覚え、入力を変えたあとに届いた前の入力の結果は使わない。
 * 問い合わせに失敗したときは判定できなかったものとして扱う（加算せず、担当者が確認する）。
 */
export const useResearchAccess = (
  apiClient: AxiosInstance,
  chibanList: PropertyLocationRow[],
  /** 対象住所（都道府県から始まる住居表示） */
  address: string
): ResearchAccessState => {
  // 地番の内容が同じなら、行の配列が作り直されても問い合わせ直さないよう、文字列にして比べる
  const rowsKey = useMemo(
    () =>
      JSON.stringify({
        rows: getCompletePropertyRows(chibanList)
          .slice(0, MAX_ACCESS_ROWS)
          .map((row) => ({
            prefName: row.prefName.trim(),
            location: row.location.trim(),
            chibanAndKaokuNumber: row.chibanAndKaokuNumber.trim(),
          })),
        // 入力中に長すぎても API で弾かれず、地番での判定は続くよう、上限で切って送る
        address: address.trim().slice(0, MAX_ACCESS_ADDRESS_LENGTH),
      }),
    [chibanList, address]
  );
  const hasRows = getCompletePropertyRows(chibanList).length > 0;
  // useApiClient は描画のたびに新しい apiClient を返すため、effect の依存に入れると判定を問い合わせ続ける
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;
  const [result, setResult] = useState<{
    key: string;
    access: ResearchAccess;
  } | null>(null);

  useEffect(() => {
    if (!hasRows) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      const query = JSON.parse(rowsKey) as {
        rows: PropertyLocationRow[];
        address: string;
      };
      new RealEstateResearchRequestAPI(apiClientRef.current)
        .judgeAccess(query.rows, query.address)
        .then((access) => {
          if (!cancelled) setResult({ key: rowsKey, access });
        })
        .catch((error: unknown) => {
          // 回数の上限（429）は連打を止めるためのもので障害ではないため、Sentry には送らない
          if (!(isAxiosError(error) && error.response?.status === 429)) {
            Sentry.captureException(error);
          }
          if (!cancelled) {
            setResult({ key: rowsKey, access: NOT_JUDGED_ACCESS });
          }
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [hasRows, rowsKey]);

  if (!hasRows) return { access: NOT_JUDGED_ACCESS, loading: false };
  if (result?.key !== rowsKey) {
    return { access: NOT_JUDGED_ACCESS, loading: true };
  }
  return { access: result.access, loading: false };
};
